import React, { useMemo, useState } from "react";
import DrillChips from "./DrillChips";
import { ValueSharePill, GrowthPill, RowIcons } from "./MetricPills";
import "./revenue.css";

/** Chip labels (different from left-nav) */
const BREAKDOWN_LABEL = {
  geo: "Geography",
  business: "Business Segment",
  product: "Product Category",
  customer: "Customer Type",
  channel: "Channel Type",
  application: "Application Market",
};

/** Canonical order to match figma */
const CANONICAL_ORDER = [
  "business",
  "product",
  "customer",
  "channel",
  "application",
  "geo",
];

export default function DrillList({
  node,
  allDims,
  path = [],
  depth = 0,
  /** flag: show AI icon only for the first/root level of THIS list */
  showAIAtThisLevel = false,
  onAIClick,
}) {
  // Remaining dimensions available at this level
  const remaining = useMemo(() => {
    const used = new Set(path);
    const pool = (allDims || []).filter((d) => !used.has(d.key));
    const byKey = new Map(pool.map((d) => [d.key, d]));
    return CANONICAL_ORDER
      .map((k) => byKey.get(k))
      .filter(Boolean)
      .map((d) => ({ ...d, label: BREAKDOWN_LABEL[d.key] || d.label }));
  }, [allDims, path]);

  // Default: first available breakdown
  const [activeKey, setActiveKey] = useState(remaining[0]?.key);

  // Rows for the selected breakdown
  const rows = useMemo(
    () => (activeKey ? node?.breakdowns?.[activeKey] || [] : []),
    [node, activeKey]
  );

  return (
    <div className={`rail rail-${Math.min(depth, 3)}`}>
      {/* Chip strip */}
      {remaining.length > 0 && (
        <DrillChips tabs={remaining} value={activeKey} onChange={setActiveKey} />
      )}

      {rows.length === 0 && remaining.length > 0 && (
        <div className="text-muted ms-2 mb-2" style={{ fontSize: 12 }}>
          Choose a breakdown above to view details.
        </div>
      )}

      {rows.map((r, idx) => (
        <DrillRow
          key={`${activeKey}-${idx}-${r.label}`}
          row={r}
          allDims={allDims}
          nextPath={[...path, activeKey]}
          depth={depth}
          /* show AI only on this list’s first level */
          showAIAtThisLevel={showAIAtThisLevel}
          onAIClick={onAIClick}
        />
      ))}
    </div>
  );
}


function DrillRow({ row, allDims, nextPath, depth, showAIAtThisLevel, onAIClick }) {
  const remainingForChild = (allDims || []).filter((d) => !nextPath.includes(d.key));
  const hasPotential = remainingForChild.length > 0;
  const [open, setOpen] = useState(false);

  return (
    <div className="drill-block">
      <div
        className={`rev-row ${open ? "open" : ""}`}
        role="button"
        onClick={() => hasPotential && setOpen((v) => !v)}
      >
        <div className="label">
          {hasPotential && (
            <i className={`ri-arrow-${open ? "up" : "down"}-s-line me-2 icon_down_up_14`} />
          )}
          {row.label}
        </div>

        <div className="right">
          <ValueSharePill value={row.value} sharePct={row.sharePct} />
          <GrowthPill growthPct={row.growthPct} />
          <RowIcons
            showInfo                
            showAI={!!showAIAtThisLevel && depth === 0}  // AI only on the first level
            onAIClick={() => {
              if (depth === 0) onAIClick?.(row.label);
            }}
            onInfoClick={(e) => {
              e.stopPropagation();
              // TODO: open your info tooltip/modal here
            }}
            sources={row.sources}
            baseFacts={row.baseFacts}
            nodeLabel={row.label}
          />
        </div>
      </div>

      {open && hasPotential && (
        <div className={`rail rail-${Math.min(depth + 1, 3)} mt-1`}>
          <DrillList
            node={row}
            allDims={allDims}
            path={nextPath}
            depth={depth + 1}
            // children don't show AI (you can flip this if you want)
            showAIAtThisLevel={false}
            onAIClick={onAIClick}
          />
        </div>
      )}
    </div>
  );
}